import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Divider,
  Tooltip,
  Alert,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/system';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CompareIcon from '@mui/icons-material/Compare';
import { supabase } from '../supabaseClient';
import { Logo } from './Logo';
import ImageCompare from './ImageCompare';
import CommentSection from './CommentSection';

const StyledWatermark = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.2,
  pointerEvents: 'none',
  transform: 'rotate(-30deg)',
  fontSize: '2rem',
  fontWeight: 'bold',
  color: theme.palette.mode === 'dark' ? '#fff' : '#000',
  textShadow: '0 0 5px rgba(0,0,0,0.3)',
  zIndex: 10
}));

// No need to add a custom watermark component since we're applying the watermark
// directly to the image before sharing. The image already has the watermark baked in.

const SharedItemViewer = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [originalImage, setOriginalImage] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [propertyImages, setPropertyImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (shareId) {
      fetchShareData();
    }
  }, [shareId]);

  const fetchShareData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First check if the share exists and if it requires a password
      const { data, error } = await supabase
        .from('shared_links')
        .select('id, password')
        .eq('id', shareId)
        .single();
      
      if (error) throw error;
      
      if (data.password) {
        setPasswordRequired(true);
        setLoading(false);
        return;
      }
      
      // If no password required, fetch the full share data
      await fetchFullShareData();
      
    } catch (err) {
      console.error('Error fetching share data:', err);
      setError('Bu paylaşım bağlantısı geçersiz veya süresi dolmuş.');
      setLoading(false);
    }
  };

  const fetchFullShareData = async () => {
    try {
      setLoading(true);
      
      // Fetch the share data
      const { data, error } = await supabase
        .from('shared_links')
        .select('*')
        .eq('id', shareId)
        .single();
      
      if (error) throw error;
      
      // Check if the share has expired
      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        setError('Bu paylaşım bağlantısının süresi dolmuş.');
        setLoading(false);
        return;
      }
      
      setShareData(data);
      
      // Increment view count
      await supabase
        .from('shared_links')
        .update({ view_count: (data.view_count || 0) + 1 })
        .eq('id', shareId);
      
      // If it's an image, fetch the original image if available
      if (data.item_type === 'image') {
        const { data: imageData, error: imageError } = await supabase
          .from('images')
          .select('original_image_url')
          .eq('id', data.item_id)
          .single();
        
        if (!imageError && imageData?.original_image_url) {
          setOriginalImage(imageData.original_image_url);
        }
      }
      
      // If it's a property, fetch all images for this property
      if (data.item_type === 'property') {
        try {
          // Create the default image from the thumbnail
          const thumbnailImage = {
            id: 'thumbnail',
            url: data.thumbnail_url,
            prompt: data.item_name || 'Property Image'
          };
          
          // Check if we have watermarked images in the share data
          if (data.has_watermark && data.custom_watermark && data.watermarked_images && data.watermarked_images.length > 0) {
            console.log("Using watermarked property images:", data.watermarked_images);
            
            // Parse the watermarked_images if it's a string (JSON)
            let parsedWatermarkedImages = data.watermarked_images;
            if (typeof data.watermarked_images === 'string') {
              try {
                parsedWatermarkedImages = JSON.parse(data.watermarked_images);
              } catch (e) {
                console.error("Error parsing watermarked images JSON:", e);
              }
            }
            
            // Create an array of watermarked images
            const watermarkedImages = Array.isArray(parsedWatermarkedImages) 
              ? parsedWatermarkedImages.map(img => ({
                  id: img.id,
                  url: img.watermarkedUrl,
                  prompt: 'Watermarked Property Image',
                  originalUrl: img.originalUrl
                }))
              : [];
            
            console.log("Processed watermarked images:", watermarkedImages);
            
            if (watermarkedImages.length > 0) {
              // Make sure the thumbnail is included
              const thumbnailIncluded = watermarkedImages.some(img => 
                img.url === thumbnailImage.url || img.originalUrl === thumbnailImage.url
              );
              
              // If not included, add it to the beginning
              const allImages = thumbnailIncluded ? 
                watermarkedImages : 
                [thumbnailImage, ...watermarkedImages];
                
              setPropertyImages(allImages);
              setSelectedImage(allImages[0]);
            } else {
              // Fallback to just the thumbnail if we couldn't process the watermarked images
              setPropertyImages([thumbnailImage]);
              setSelectedImage(thumbnailImage);
            }
          } else {
            // No watermarked images, fetch regular property images
            const { data: propertyImages, error: imagesError } = await supabase
              .from('images')
              .select('id, url, prompt')
              .eq('property_id', data.item_id)
              .order('created_at', { ascending: false });
            
            if (!imagesError && propertyImages && propertyImages.length > 0) {
              // Check if thumbnail is already included in fetched images
              const thumbnailIncluded = propertyImages.some(img => img.url === thumbnailImage.url);
              
              // If not included, add it to the beginning
              const allImages = thumbnailIncluded ? 
                propertyImages : 
                [thumbnailImage, ...propertyImages];
                
              setPropertyImages(allImages);
              setSelectedImage(allImages[0]);
            } else {
              // Just use the thumbnail
              setPropertyImages([thumbnailImage]);
              setSelectedImage(thumbnailImage);
            }
          }
        } catch (err) {
          console.error('Error handling property images:', err);
          // Fallback to single image
          const singleImage = {
            id: 'thumbnail',
            url: data.thumbnail_url,
            prompt: data.item_name || 'Property Image'
          };
          setPropertyImages([singleImage]);
          setSelectedImage(singleImage);
          
          // Log detailed error for debugging
          console.log('Detailed error:', JSON.stringify(err));
        }
      }
      
    } catch (err) {
      console.error('Error fetching full share data:', err);
      setError('Paylaşım detayları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      setPasswordError(false);
      
      // Check if the password is correct
      const { data, error } = await supabase
        .from('shared_links')
        .select('password')
        .eq('id', shareId)
        .single();
      
      if (error) throw error;
      
      if (data.password !== password) {
        setPasswordError(true);
        return;
      }
      
      // Password is correct, fetch the full share data
      await fetchFullShareData();
      setPasswordRequired(false);
      
    } catch (err) {
      console.error('Error checking password:', err);
      setError('Şifre kontrol edilirken bir hata oluştu.');
    }
  };

  const handleDownload = async () => {
    if (!shareData?.thumbnail_url) return;
    
    try {
      // Create a temporary anchor element
      const link = document.createElement('a');
      
      // Fetch the image first to handle CORS issues
      fetch(shareData.thumbnail_url)
        .then(response => response.blob())
        .then(blob => {
          // Create a blob URL for the image
          const blobUrl = URL.createObjectURL(blob);
          
          // Set up the download
          link.href = blobUrl;
          link.download = `${shareData.item_name || 'sanalyapi-image'}.png`;
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          }, 100);
        });
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const getPermissionIcon = () => {
    if (!shareData) return null;
    
    switch (shareData.permission) {
      case 'view':
        return <VisibilityIcon fontSize="small" />;
      case 'comment':
        return <CommentIcon fontSize="small" />;
      case 'edit':
        return <EditIcon fontSize="small" />;
      default:
        return <VisibilityIcon fontSize="small" />;
    }
  };

  const getExpirationText = () => {
    if (!shareData?.expires_at) return 'Süresiz';
    
    const expDate = new Date(shareData.expires_at);
    const now = new Date();
    const diffTime = expDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Süresi doldu';
    if (diffDays === 1) return 'Bugün sona eriyor';
    if (diffDays < 7) return `${diffDays} gün kaldı`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta kaldı`;
    return `${Math.floor(diffDays / 30)} ay kaldı`;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <Logo sx={{ mb: 4 }} />
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <Logo sx={{ mb: 4 }} />
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
        >
          Ana Sayfaya Dön
        </Button>
      </Box>
    );
  }

  if (passwordRequired) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <Logo sx={{ mb: 4 }} />
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            maxWidth: 400, 
            width: '100%',
            borderRadius: '12px'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Şifre Korumalı İçerik
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bu içeriği görüntülemek için şifre gerekiyor.
            </Typography>
          </Box>
          
          <TextField
            label="Şifre"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError}
            helperText={passwordError ? "Yanlış şifre" : ""}
            sx={{ mb: 3 }}
          />
          
          <Button 
            variant="contained" 
            fullWidth 
            onClick={handlePasswordSubmit}
            disabled={!password}
          >
            Erişim Sağla
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        backgroundColor: theme => theme.palette.background.paper,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Logo sx={{ mr: 2 }} />
          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {shareData.item_name || (shareData.item_type === 'property' ? 'Mülk' : 'Görsel')}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            icon={getPermissionIcon()}
            label={shareData.permission === 'view' ? 'Görüntüleme' : 
                  shareData.permission === 'comment' ? 'Yorum' : 'Düzenleme'}
            size="small"
            color="primary"
            variant="outlined"
          />
          
          <Chip 
            icon={<AccessTimeIcon fontSize="small" />}
            label={getExpirationText()}
            size="small"
            variant="outlined"
          />
          
          {shareData.allow_download && (
            <Tooltip title="İndir">
              <IconButton onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
          
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            size="small"
          >
            Ana Sayfa
          </Button>
        </Box>
      </Box>
      
      {/* Content */}
      <Box sx={{ 
        flex: 1, 
        p: { xs: 2, sm: 4 },
        backgroundColor: theme => theme.palette.mode === 'dark' ? '#121212' : '#f5f5f5',
      }}>
        {shareData.item_type === 'image' ? (
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 3, 
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <Box sx={{ position: 'relative' }}>
                {shareData.has_watermark && (
                  <StyledWatermark>
                    SANALYAPI.COM
                  </StyledWatermark>
                )}
                
                <img 
                  src={shareData.thumbnail_url} 
                  alt={shareData.item_name || 'Shared image'}
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    borderRadius: '8px',
                    display: 'block'
                  }}
                />
              </Box>
              
              {originalImage && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => setShowCompare(true)}
                    startIcon={<CompareIcon />}
                  >
                    Orijinal ile Karşılaştır
                  </Button>
                </Box>
              )}
              
              {shareData.permission === 'comment' || shareData.permission === 'edit' ? (
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowComments(!showComments)}
                    startIcon={<CommentIcon />}
                    sx={{ mb: 2 }}
                  >
                    {showComments ? 'Yorumları Gizle' : 'Yorumları Göster'}
                  </Button>
                  
                  {showComments && (
                    <CommentSection 
                      itemId={shareData.item_id}
                      itemType={shareData.item_type}
                      shareId={shareId}
                      canEdit={shareData.permission === 'edit'}
                    />
                  )}
                </Box>
              ) : null}
            </Paper>
          </Box>
        ) : shareData.item_type === 'property' ? (
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              {shareData.item_name || 'Mülk Detayları'}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    borderRadius: '12px',
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%'
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    {shareData.has_watermark && (
                      <StyledWatermark>
                        SANALYAPI.COM
                      </StyledWatermark>
                    )}
                    
                    {selectedImage ? (
                      <img 
                        src={selectedImage.url} 
                        alt={selectedImage.prompt || shareData.item_name || 'Property image'}
                        style={{ 
                          width: '100%', 
                          height: 'auto', 
                          borderRadius: '8px',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: '100%',
                        height: 300,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography color="text.secondary">
                          Görsel bulunamadı
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Image thumbnails gallery */}
                  {propertyImages.length > 1 && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 1,
                      mt: 2,
                      justifyContent: 'center'
                    }}>
                      {propertyImages.map((image) => (
                        <Box 
                          key={image.id}
                          onClick={() => setSelectedImage(image)}
                          sx={{
                            width: 80,
                            height: 60,
                            borderRadius: '4px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: theme => selectedImage?.id === image.id 
                              ? `2px solid ${theme.palette.primary.main}` 
                              : `1px solid ${theme.palette.divider}`,
                            opacity: selectedImage?.id === image.id ? 1 : 0.7,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              opacity: 1,
                              transform: 'scale(1.05)'
                            }
                          }}
                        >
                          <img 
                            src={image.url} 
                            alt={image.prompt || 'Property image'}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover' 
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    borderRadius: '12px',
                    height: '100%'
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Mülk Bilgileri
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Oluşturulma Tarihi
                    </Typography>
                    <Typography variant="body1">
                      {new Date(shareData.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  {shareData.permission === 'comment' || shareData.permission === 'edit' ? (
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setShowComments(!showComments)}
                        startIcon={<CommentIcon />}
                        fullWidth
                      >
                        {showComments ? 'Yorumları Gizle' : 'Yorumları Göster'}
                      </Button>
                    </Box>
                  ) : null}
                </Paper>
              </Grid>
            </Grid>
            
            {showComments && (
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  borderRadius: '12px',
                  mt: 3
                }}
              >
                <CommentSection 
                  itemId={shareData.item_id}
                  itemType={shareData.item_type}
                  shareId={shareId}
                  canEdit={shareData.permission === 'edit'}
                />
              </Paper>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography>Desteklenmeyen içerik türü</Typography>
          </Box>
        )}
      </Box>
      
      {/* Footer */}
      <Box sx={{ 
        p: 2, 
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        backgroundColor: theme => theme.palette.background.paper,
        textAlign: 'center'
      }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Sanalyapı - Tüm hakları saklıdır
        </Typography>
      </Box>
      
      {/* Image Compare Modal */}
      <Dialog
        open={showCompare}
        onClose={() => setShowCompare(false)}
        maxWidth="xl"
        fullWidth
      >
        {showCompare && originalImage && shareData?.thumbnail_url && (
          <ImageCompare
            originalImage={originalImage}
            generatedImage={shareData.thumbnail_url}
            onClose={() => setShowCompare(false)}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default SharedItemViewer;
